import { computed } from 'vue';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useModuleConfig } from '@rebuild/hooks/data/useModuleConfig';
import { useStockVO } from '@rebuild/hooks/useStockVO';
import { formatDefault, convertNumberToCNUnit } from '@/utils/api';
import i18n from '@/locales';


const useCurrentReactive = () => {
    const { isHKStock } = useStockVO();

    // 数据层
    const { dividend_plan_summary, dividend_plan_chart, dividend_plan_table } = useModuleData();
    const { loadModuleConfig, getDisplayIndex } = useModuleConfig();

    const refreshModule = async () => {
        await loadModuleConfig('dividend_plan');
        await Promise.all([dividend_plan_summary.refresh(), dividend_plan_chart.refresh(), dividend_plan_table.refresh()]);
    };

    // 统一的数据计算属性，避免重复取值
    const summaryData = computed(() => dividend_plan_summary.data?.data || {});
    const chartRawData = computed(() => dividend_plan_chart.data?.data || []);
    const tableRawData = computed(() => dividend_plan_table.data?.data || []);

    // 处理汇总数据
    const summary = computed(() => {
        const obj = summaryData.value;
        return {
            ...obj,
            ...convertNumberToCNUnit(parseFloat(obj.dividendTotal)),
            dividendYield:
                obj.dividendTotalLast12Months && obj.totalMv
                    ? ((obj.dividendTotalLast12Months / obj.totalMv) * 100).toFixed(2) + '%'
                    : '-'
        };
    });

    // 处理图表数据
    const chartData = computed(() => {
        return [
            {
                type: 'bar',
                data: chartRawData.value.map(ele => [ele.year.toString(), ele.dividendTotal])
            }
        ];
    });

    // 处理表格数据
    const tableData = computed(() => {
        return tableRawData.value.map(ele => {
            ele.publishYear = ele.publishDate.split(/-|\//)[0];
            return ele;
        });
    });

    // 配置数据
    const showSummary = computed(() => getDisplayIndex.value);

    // 使用i18n.t()替代proxy.$t()
    const summaryLabel = computed(() => {
        if (isHKStock.value) return i18n.t('enterprise.recent-dividend-scheme');
        const values = summary.value;
        return values.unit ? `${i18n.t('enterprise.total-dividend')}(${values.unit})` : i18n.t('enterprise.total-dividend');
    });

    const summaryValue = computed(() => {
        const values = summary.value;
        const { dividendCash, value } = values;
        return isHKStock.value ? (isNaN(dividendCash) ? '-' : dividendCash) : isNaN(value) ? '-' : value;
    });

    const formatterTableCol = (row, column, cellValue) => {
        // 兼容 el-table (column.property) 和 MiniTable (column.prop 或直接传 cellValue)
        const prop = column.prop || column.property;
        const value = cellValue !== undefined ? cellValue : (prop ? row[prop] : undefined);
        return formatDefault(value);
    };

    return {
        summary,
        chartData,
        tableData,
        showSummary,
        summaryLabel,
        summaryValue,
        isHKStock,
        formatterTableCol,
        refreshModule
    };
}

export { useCurrentReactive };